import type { InsightKind } from "@ti/core";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { CheckCheck, Lightbulb, Sparkles } from "lucide-react";
import { useMemo } from "react";
import { z } from "zod";
import { PageHeader } from "@/components/domain/misc";
import { ErrorPanel, Page } from "@/components/layout/page";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { EmptyState, Kbd, Skeleton } from "@/components/ui/misc";
import { FilterPill } from "@/features/coverage/filter-pill";
import { AskPanel } from "@/features/insights/ask-panel";
import { INSIGHT_KIND, INSIGHT_KINDS, InsightCard } from "@/features/insights/insight-card";
import { q, useUpdateInsight } from "@/lib/api";

export const Route = createFileRoute("/_app/insights")({
  validateSearch: z.object({ kind: z.enum(INSIGHT_KINDS as [InsightKind, ...InsightKind[]]).optional().catch(undefined) }),
  component: InsightsPage,
});

function InsightsPage() {
  const { kind } = Route.useSearch();
  const navigate = Route.useNavigate();
  const insights = useQuery(q.insights());
  const agents = useQuery(q.agents());
  const update = useUpdateInsight();
  const names = useMemo(() => new Map(agents.data?.map((a) => [a.id, a.name])), [agents.data]);
  const counts = useMemo(() => {
    const m = new Map<InsightKind, number>();
    for (const i of insights.data ?? []) m.set(i.kind, (m.get(i.kind) ?? 0) + 1);
    return m;
  }, [insights.data]);
  // New first, then newest.
  const rows = useMemo(
    () => (insights.data ?? []).filter((i) => !kind || i.kind === kind).sort((a, b) => Number(b.status === "new") - Number(a.status === "new") || b.createdAt.localeCompare(a.createdAt)),
    [insights.data, kind],
  );
  const unread = (insights.data ?? []).filter((i) => i.status === "new");

  return (
    <Page>
      <PageHeader
        title="Insights"
        description="What changed this week and with each release, across use cases, funnels, outcomes, pain points and signals. Ask anything about your agents' data above the feed."
      />

      <Card className="mb-5">
        <CardHeader
          title={
            <span className="inline-flex items-center gap-1.5">
              <Sparkles className="size-3.5 text-accent" aria-hidden /> Ask
            </span>
          }
          description={
            <>
              Also from anywhere with <Kbd>⌘J</Kbd>
            </>
          }
        />
        <CardBody>
          <AskPanel />
        </CardBody>
      </Card>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h2 className="mr-2 text-xs font-semibold uppercase tracking-wide text-fg-3">Feed</h2>
        <div role="group" aria-label="Filter by kind" className="flex flex-wrap gap-1.5">
          <FilterPill active={!kind} onClick={() => void navigate({ search: {}, replace: true, resetScroll: false })} label="All" count={insights.data?.length ?? 0} />
          {INSIGHT_KINDS.filter((k) => counts.get(k)).map((k) => (
            <FilterPill key={k} active={kind === k} icon={INSIGHT_KIND[k].icon} onClick={() => void navigate({ search: kind === k ? {} : { kind: k }, replace: true, resetScroll: false })} label={INSIGHT_KIND[k].label} count={counts.get(k) ?? 0} />
          ))}
        </div>
        {unread.length ? (
          <Button
            size="sm"
            variant="ghost"
            className="ml-auto"
            disabled={update.isPending}
            onClick={() => {
              for (const i of unread) update.mutate({ id: i.id, status: "read" });
            }}
          >
            <CheckCheck /> Mark {unread.length} as read
          </Button>
        ) : null}
      </div>

      {insights.error ? (
        <ErrorPanel error={insights.error} reset={() => void insights.refetch()} />
      ) : insights.isPending ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} className="h-40" />
          ))}
        </div>
      ) : rows.length ? (
        <ul className="flex flex-col gap-3">
          {rows.map((i) => (
            <li key={i.id}>
              <InsightCard insight={i} agentNames={names} />
            </li>
          ))}
        </ul>
      ) : (
        <Card>
          <EmptyState icon={<Lightbulb />} title={kind ? "No insights of this kind" : "No insights yet"} description="A nightly job compares this week with the last, and each release with the previous version, and writes findings here." />
        </Card>
      )}
    </Page>
  );
}
